import React, { useMemo, useState, useCallback } from 'react';
import { useFavorites } from '../../hooks/api/useFavorites';
import { Header } from '../../components/layout/header';
import { Footer } from '../../components/layout/footer';
import { AudioExperienceTile, AudioExperienceTileSkeleton } from '../../components/audio-experience-tile';
import { VideoExperienceTile, VideoExperienceTileSkeleton } from '../../components/video-experience-tile';
import { ResourceTile, ResourceTileSkeleton } from '../../components/resource-tile';
import { PeerAvatar, PeerAvatarSkeleton } from '../../components/peer-avatar';
import { ContentSection } from '../../components/content-section';
import { Content, Resource, People } from '../../hooks/api/useExperiences';
import { InlineSpinner } from '../../components/common/spinner';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { usePeersonaAccess } from '../../hooks/utils/usePeersonaAccess';
const INITIAL_ITEMS = 6;
const LOAD_MORE_ITEMS = 6;

/** Tighter spacing on My Favorites: section margin, row height, no stretch on carousel items, smaller gap */
const FAVORITES_SECTION_CLASS =
  '!mb-3 md:!mb-12 [&>div.overflow-x-auto]:!h-auto [&>div.overflow-x-auto]:!min-h-0 [&>div.overflow-x-auto]:items-start [&>div.overflow-x-auto]:!gap-3 [&>div.overflow-x-auto]:md:!gap-6';

const MyFavoritesPage = () => {
  const { data: favoritesData, isLoading } = useFavorites();
  const dfMdMedia = useMediaQuery('md');
  const { isPeerPeersonaEnabled } = usePeersonaAccess();

  const listenerRoles = useMemo(() => favoritesData?.data?.listener_roles || [], [favoritesData]);
  const resources = useMemo(
    () => [...(favoritesData?.data?.client_resources || []), ...(favoritesData?.data?.global_resources || [])],
    [favoritesData],
  );
  const allContent = useMemo(() => favoritesData?.data?.listener_audio || [], [favoritesData]);

  const videoContent = useMemo(() => allContent.filter((item: Content) => !!item.video_id), [allContent]);
  const audioContent = useMemo(() => allContent.filter((item: Content) => !item.video_id), [allContent]);

  // State for tracking items shown per section
  const [videoItemsShown, setVideoItemsShown] = useState(INITIAL_ITEMS);
  const [audioItemsShown, setAudioItemsShown] = useState(INITIAL_ITEMS);
  const [resourceItemsShown, setResourceItemsShown] = useState(INITIAL_ITEMS);
  const [peerItemsShown, setPeerItemsShown] = useState(INITIAL_ITEMS);

  const handleLoadMoreVideos = useCallback(() => {
    setVideoItemsShown((prev) => Math.min(prev + LOAD_MORE_ITEMS, videoContent.length));
  }, [videoContent.length]);

  const handleLoadMoreAudio = useCallback(() => {
    setAudioItemsShown((prev) => Math.min(prev + LOAD_MORE_ITEMS, audioContent.length));
  }, [audioContent.length]);

  const handleLoadMoreResources = useCallback(() => {
    setResourceItemsShown((prev) => Math.min(prev + LOAD_MORE_ITEMS, resources.length));
  }, [resources.length]);

  const handleLoadMorePeers = useCallback(() => {
    setPeerItemsShown((prev) => Math.min(prev + LOAD_MORE_ITEMS, listenerRoles.length));
  }, [listenerRoles.length]);

  if (isLoading && !favoritesData) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        <Header />
        <div className={`flex-1 ${!dfMdMedia ? 'pt-[76px]' : ''}`}>
          <div className="flex h-full w-full items-center justify-center">
            <InlineSpinner />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <div className={`flex-1 ${!dfMdMedia ? 'pt-[76px]' : ''}`}>
        <div className="max-w-page mx-auto px-6 py-8">
          <h1 className="font-open-sans mb-6 text-3xl font-semibold leading-[60px] text-black">My Favorites</h1>

          {videoContent.length > 0 || isLoading ? (
            <ContentSection
              className={FAVORITES_SECTION_CLASS}
              title="Favorite Video Stories"
              items={videoContent}
              itemsShown={videoItemsShown}
              onLoadMore={videoContent.length > videoItemsShown ? handleLoadMoreVideos : undefined}
              isLoading={isLoading && videoContent.length === 0}
              renderItem={(item: Content | undefined, index) =>
                item ? (
                  <VideoExperienceTile key={item.id || index} experience={item} />
                ) : (
                  <VideoExperienceTileSkeleton key={`skeleton-${index}`} />
                )
              }
            />
          ) : (
            <div className="mb-12">
              <h2 className="font-albert-sans mb-4 text-xl font-semibold leading-8 text-black">
                Favorite Video Stories
              </h2>
              <p className="font-open-sans text-section-header-slate text-base">
                You haven't favorited any video stories yet.
              </p>
            </div>
          )}

          {audioContent.length > 0 || isLoading ? (
            <ContentSection
              className={FAVORITES_SECTION_CLASS}
              title="Favorite Audio Stories"
              items={audioContent}
              itemsShown={audioItemsShown}
              onLoadMore={audioContent.length > audioItemsShown ? handleLoadMoreAudio : undefined}
              isLoading={isLoading && audioContent.length === 0}
              renderItem={(item: Content | undefined, index) =>
                item ? (
                  <AudioExperienceTile key={item.id || index} experience={item} />
                ) : (
                  <AudioExperienceTileSkeleton key={`skeleton-${index}`} />
                )
              }
            />
          ) : (
            <div className="mb-12">
              <h2 className="font-albert-sans mb-4 text-xl font-semibold leading-8 text-black">
                Favorite Audio Stories
              </h2>
              <p className="font-open-sans text-section-header-slate text-base">
                You haven't favorited any audio stories yet.
              </p>
            </div>
          )}

          {resources.length > 0 || isLoading ? (
            <ContentSection
              className={FAVORITES_SECTION_CLASS}
              title="Favorite Resources"
              items={resources}
              itemsShown={resourceItemsShown}
              onLoadMore={resources.length > resourceItemsShown ? handleLoadMoreResources : undefined}
              isLoading={isLoading && resources.length === 0}
              renderItem={(resource: Resource | undefined, index) =>
                resource ? (
                  <ResourceTile
                    key={resource.id || index}
                    id={resource.id}
                    resource={resource}
                    image={resource.image_url}
                    resourceName={resource.name}
                    caption={resource.description}
                    tagGroupNames={(resource as any).tag_group_names}
                    isGlobal={!resource.client_id}
                  />
                ) : (
                  <ResourceTileSkeleton key={`skeleton-${index}`} />
                )
              }
            />
          ) : (
            <div className="mb-12">
              <h2 className="font-albert-sans mb-4 text-xl font-semibold leading-8 text-black">Favorite Resources</h2>
              <p className="font-open-sans text-section-header-slate text-base">
                You haven't favorited any resources yet.
              </p>
            </div>
          )}

          {listenerRoles.length > 0 || isLoading ? (
            <ContentSection
              className={FAVORITES_SECTION_CLASS}
              title="Favorite Peer Profiles"
              items={listenerRoles}
              itemsShown={peerItemsShown}
              onLoadMore={listenerRoles.length > peerItemsShown ? handleLoadMorePeers : undefined}
              isLoading={isLoading && listenerRoles.length === 0}
              renderItem={(peer: People | undefined, index) =>
                peer ? (
                  <PeerAvatar
                    key={peer.listener_role_id || index}
                    peer={peer}
                    isPeersona={isPeerPeersonaEnabled(peer.listener_role_id, peer.chatbot_id)}
                  />
                ) : (
                  <PeerAvatarSkeleton key={`skeleton-${index}`} />
                )
              }
            />
          ) : (
            <div className="mb-12">
              <h2 className="font-albert-sans mb-4 text-xl font-semibold leading-8 text-black">
                Favorite Peer Profiles
              </h2>
              <p className="font-open-sans text-section-header-slate text-base">
                You haven't favorited any peer profiles yet.
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MyFavoritesPage;
